import { useState } from 'react';
import { Button, TextInput, Box, Skeleton, Stack, Paper } from '@mantine/core';
import useSWR from 'swr';
import { getFetcher } from '@/api/fetchers';
import { useForm } from 'react-hook-form';
import { ChangeEmailModal } from '@/components/Modal/ChangeEmailModal';
import { ChangePasswordModal } from '@/components/Modal/ChangePasswordModal';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { formatDate } from '@/utils/Date';
import { type UserInfo } from '@/schemas/API/GET/account/response';

export const AccountPage = (): JSX.Element => {
  const { data: userInfo } = useSWR<UserInfo>('users/me', getFetcher);
  const { data: sexMap } = useSWR<Mapping>('mappings/sexes', getFetcher);

  const [isChangeEmailModalOpen, setChangeEmailModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setChangePasswordModalOpen] =
    useState(false);
  const { register } = useForm();

  const openChangeEmailModal = () => setChangeEmailModalOpen(true);
  const closeChangeEmailModal = () => setChangeEmailModalOpen(false);
  const openChangePasswordModal = () => setChangePasswordModalOpen(true);
  const closeChangePasswordModal = () => setChangePasswordModalOpen(false);

  return (
    <Stack className="p-8">
      <PageHeader name="Můj účet" />

      <Skeleton visible={userInfo == null}>
        <Box className="flex flex-wrap gap-4">
          <Paper withBorder radius="md" className="flex-grow p-4">
            <TextInput
              {...register('firstname')}
              label="Jméno"
              defaultValue={userInfo?.info.firstName}
              readOnly
              styles={{
                input: {
                  backgroundColor: '#f3f4f6',
                  borderColor: 'transparent',
                  borderRadius: '0.5rem',
                  '&:focus': { outline: 'none', boxShadow: 'none' },
                },
                label: {
                  fontSize: '1.325rem',
                },
              }}
              className="w-full border-none focus:ring-0 mb-4 text-lg"
            />
            <TextInput
              {...register('lastname')}
              label="Příjmení"
              defaultValue={userInfo?.info.lastName}
              readOnly
              styles={{
                input: {
                  backgroundColor: '#f3f4f6',
                  borderColor: 'transparent',
                  borderRadius: '0.5rem',
                  '&:focus': { outline: 'none', boxShadow: 'none' },
                },
                label: {
                  fontSize: '1.325rem',
                },
              }}
              className="w-full border-none focus:ring-0 mb-4 text-lg"
            />
            <TextInput
              {...register('birthdate')}
              label="Datum narození"
              defaultValue={userInfo ? formatDate(userInfo.info.birthdate) : ''}
              readOnly
              styles={{
                input: {
                  backgroundColor: '#f3f4f6',
                  borderColor: 'transparent',
                  borderRadius: '0.5rem',
                  '&:focus': { outline: 'none', boxShadow: 'none' },
                },
                label: {
                  fontSize: '1.325rem',
                },
              }}
              className="w-full border-none focus:ring-0 mb-4 text-lg"
            />
            <TextInput
              {...register('sex')}
              label="Pohlaví"
              defaultValue={userInfo ? sexMap?.[userInfo.info.sex] : ''}
              readOnly
              styles={{
                input: {
                  backgroundColor: '#f3f4f6',
                  borderColor: 'transparent',
                  borderRadius: '0.5rem',
                  '&:focus': { outline: 'none', boxShadow: 'none' },
                },
                label: {
                  fontSize: '1.325rem',
                },
              }}
              className="w-full border-none focus:ring-0 text-lg"
            />
          </Paper>

          <Paper
            withBorder
            radius="md"
            className="flex-grow flex flex-col justify-evenly gap-4 p-4"
          >
            <Box>
              <TextInput
                {...register('email')}
                label="Email"
                defaultValue={userInfo?.email}
                readOnly
                styles={{
                  input: {
                    backgroundColor: '#f3f4f6',
                    borderColor: 'transparent',
                    borderRadius: '0.5rem',
                    '&:focus': { outline: 'none', boxShadow: 'none' },
                  },
                  label: {
                    fontSize: '1.325rem',
                  },
                }}
                className="border-none focus:ring-0 text-lg mb-4"
              />
              <Button
                variant="outline"
                onClick={openChangeEmailModal}
                className="w-full rounded-lg text-lg"
              >
                Změnit email
              </Button>
            </Box>
            <Box>
              <TextInput
                {...register('password')}
                label="Heslo"
                type="password"
                placeholder="********"
                readOnly
                styles={{
                  input: {
                    backgroundColor: '#f3f4f6',
                    borderColor: 'transparent',
                    borderRadius: '0.5rem',
                    '&:focus': { outline: 'none', boxShadow: 'none' },
                  },
                  label: {
                    fontSize: '1.325rem',
                  },
                }}
                className="border-none focus:ring-0 text-lg mb-4"
              />
              <Button
                variant="outline"
                onClick={openChangePasswordModal}
                className="w-full rounded-lg text-lg"
              >
                Změnit heslo
              </Button>
            </Box>
          </Paper>
        </Box>
      </Skeleton>

      <ChangeEmailModal
        opened={isChangeEmailModalOpen}
        onClose={closeChangeEmailModal}
        currentEmail={userInfo ? userInfo.email : ''}
      />
      <ChangePasswordModal
        opened={isChangePasswordModalOpen}
        onClose={closeChangePasswordModal}
      />
    </Stack>
  );
};
